import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';

/**
 * Hook customizado para geração de relatórios PDF
 */
const usePdfReports = ({ showSuccessModal, showErrorModal, setLoading, openPdfViewer }) => {
  const token = localStorage.getItem('token');

  /**
   * Função para gerar relatórios individuais (sem filtros)
   * Usa a mesma lógica do código original que funcionava
   */
  const generateIndividualReport = async (endpoint, params) => {
    try {
      if (setLoading) setLoading(true);

      const response = await axios.get(
        `${API_BASE_URL.RELATORIO}/${endpoint}`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Validar se o PDF foi criado com sucesso antes de abrir
      if (response.status === 200 && response.data && response.data.size > 0) {
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        if (openPdfViewer) {
          openPdfViewer(pdfUrl, 'Relatório');
        } else {
          window.open(pdfUrl, '_blank');
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
        }

        // Não mostrar modal de sucesso quando abre automaticamente
        // O usuário já vê o PDF sendo aberto
      } else {
        throw new Error(
          'PDF não foi gerado corretamente - arquivo vazio ou inválido'
        );
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      if (showErrorModal) {
        showErrorModal('error', 'Falha ao gerar o relatório.');
      }
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  /**
   * Função para gerar relatórios com filtros
   */
  const generateFilteredReport = async (
    endpoint,
    params,
    successMessage,
    errorMessage,
    options = {}
  ) => {
    const { autoOpen = false, onSuccess, onError, filtros } = options;

    // Aplicar filtros de data aos parâmetros se fornecidos
    const finalParams = { ...params };
    if (filtros) {
      finalParams.tipo = filtros.tipo;

      switch (filtros.tipo) {
        case 'data':
          if (filtros.dataEspecifica) {
            finalParams.dataEspecifica = filtros.dataEspecifica;
          }
          break;
        case 'mes':
          if (filtros.mes) {
            finalParams.mes = filtros.mes;
          }
          break;
        case 'periodo':
          if (filtros.dataInicio && filtros.dataFim) {
            finalParams.dataInicio = filtros.dataInicio;
            finalParams.dataFim = filtros.dataFim;
          }
          break;
      }
    }

    if (setLoading) setLoading(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL.RELATORIO}/${endpoint}`,
        {
          params: finalParams,
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      if (response.status === 200 && response.data && response.data.size > 0) {
        if (autoOpen) {
          const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          const pdfUrl = URL.createObjectURL(pdfBlob);
          
          if (openPdfViewer) {
            openPdfViewer(pdfUrl, successMessage.split('\n')[0] || 'Relatório');
          } else {
            window.open(pdfUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
          }

          // Não mostrar modal de sucesso quando abre automaticamente
          // O usuário já vê o PDF sendo aberto
        } else {
          if (showSuccessModal) {
            showSuccessModal(
              `${successMessage}\nClique em OK para abrir.`,
              response.data
            );
          }
        }

        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        throw new Error('Relatório não foi gerado corretamente');
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);

      if (showErrorModal) {
        showErrorModal('error', errorMessage);
      }

      if (onError) {
        onError(error);
      }
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  /**
   * Função para criar um blob PDF e retornar a URL
   * Útil quando você quer manipular o PDF de forma customizada
   */
  const createPdfUrl = pdfData => {
    const pdfBlob = new Blob([pdfData], { type: 'application/pdf' });
    return URL.createObjectURL(pdfBlob);
  };

  /**
   * Função para baixar o PDF em vez de abrir
   */
  const downloadPdf = (pdfData, filename = 'relatorio.pdf') => {
    const pdfBlob = new Blob([pdfData], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
  };

  return {
    generateIndividualReport,
    generateFilteredReport,
    // Manter a função original para compatibilidade
    generatePdfReport: generateFilteredReport,
    createPdfUrl,
    downloadPdf,
  };
};

export default usePdfReports;
